import { 
  collection, 
  query, 
  where, 
  orderBy, 
  getDocs, 
  doc, 
  deleteDoc, 
  getDoc,
  Timestamp,
  QueryConstraint,
  limit,
  startAfter,
  DocumentSnapshot,
  documentId,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { db, authService } from './firebase';
import { 
  Order, 
  OrderFilters, 
  OrderSortConfig, 
  DeleteResult, 
  ValidationResult, 
  OrderSummary,
  OrderStatus,
  PaymentMethod,
  DeliveryMethod
} from '../models/Order';
import userService, { User } from './userService';

type GetOrdersOptions = {
  enrichUsers?: boolean;
  enrichProducts?: boolean;
};

class OrderService {
  private readonly collectionName = 'orders';
  private readonly productNameCache = new Map<string, string>();
  private readonly unknownProductName = 'Produit inconnu';

  private coerceToDate(value: unknown): Date | undefined {
    if (!value) return undefined;

    if (value instanceof Date) {
      return value;
    }

    if (value instanceof Timestamp) {
      return value.toDate();
    }

    if (typeof value === 'object') {
      const maybe = value as {
        toDate?: () => Date;
        seconds?: number;
        nanoseconds?: number;
        _seconds?: number;
        _nanoseconds?: number;
      };

      if (typeof maybe.toDate === 'function') {
        try {
          const date = maybe.toDate();
          if (date instanceof Date && !Number.isNaN(date.getTime())) {
            return date;
          }
        } catch {
        }
      }

      const seconds = typeof maybe.seconds === 'number'
        ? maybe.seconds
        : (typeof maybe._seconds === 'number' ? maybe._seconds : undefined);
      if (typeof seconds === 'number') {
        const nanos = typeof maybe.nanoseconds === 'number'
          ? maybe.nanoseconds
          : (typeof maybe._nanoseconds === 'number' ? maybe._nanoseconds : 0);
        const date = new Date(seconds * 1000 + Math.floor(nanos / 1e6));
        if (!Number.isNaN(date.getTime())) {
          return date;
        }
      }
    }

    if (typeof value === 'number') {
      const ms = value < 1e12 ? value * 1000 : value;
      const date = new Date(ms);
      return Number.isNaN(date.getTime()) ? undefined : date;
    }

    if (typeof value === 'string') {
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? undefined : date;
    }

    return undefined;
  }

  // Convertir un document Firestore en objet Order
  private convertFirestoreToOrder(doc: any): Order {
    const data = doc.data();
    const cancellationCancelledAt = this.coerceToDate(data.cancellation?.cancelledAt);
    const statusCancelledAt = this.coerceToDate(data.statusTimestamps?.cancelledAt);
    const driverCommissionAmount =
      data.driverCommissionAmount === null || data.driverCommissionAmount === undefined
        ? undefined
        : Number(data.driverCommissionAmount);
    const driverCommissionPercentageSnapshot =
      data.driverCommissionPercentageSnapshot === null || data.driverCommissionPercentageSnapshot === undefined
        ? undefined
        : Number(data.driverCommissionPercentageSnapshot);
    
    return {
      id: doc.id,
      addressId: data.addressId || '',
      allergies: data.allergies || null,
      broadcastAttempts: data.broadcastAttempts || 0,
      broadcastTimestamp: this.coerceToDate(data.broadcastTimestamp) ?? null,
      clientAddress: {
        formatted: data.clientAddress?.formatted || '',
        location: data.clientAddress?.location || [0, 0]
      },
      createdAt: this.coerceToDate(data.createdAt) ?? new Date(),
      currentOfferingDriverId: data.currentOfferingDriverId || null,
      deliveryFeeAtOrderTime: data.deliveryFeeAtOrderTime || 0,
      deliveryMethod: (() => {
        const raw = (data.deliveryMethod ?? 'delivery');
        const lower = typeof raw === 'string' ? raw.toLowerCase() : String(raw).toLowerCase();
        return (lower === 'pickup') ? 'pickup' : 'delivery';
      })(),
      deliveryPerson: data.deliveryPerson ? {
        id: data.deliveryPerson.id || '',
        firstName: data.deliveryPerson.firstName || '',
        lastName: data.deliveryPerson.lastName || '',
        phone: data.deliveryPerson.phone || ''
      } : undefined,
      driverPayout: data.driverPayout || 0,
      driverCommissionAmount: Number.isFinite(driverCommissionAmount) ? driverCommissionAmount : undefined,
      driverCommissionPercentageSnapshot: Number.isFinite(driverCommissionPercentageSnapshot)
        ? driverCommissionPercentageSnapshot
        : undefined,
      eligibleDriverQueue: data.eligibleDriverQueue || [],
      estimatedDistanceKm: data.estimatedDistanceKm || 0,
      estimatedDurationMinutes: data.estimatedDurationMinutes || 0,
      estimatedEnd: this.coerceToDate(data.estimatedEnd) ?? new Date(),
      estimatedPreparationMinutes: data.estimatedPreparationMinutes || 0,
      estimatedStart: this.coerceToDate(data.estimatedStart) ?? new Date(),
      isBroadcasting: data.isBroadcasting || false,
      isPhysicallyReady: data.isPhysicallyReady || false,
      items: data.items || [],
      offerExpiresAt: this.coerceToDate(data.offerExpiresAt) ?? new Date(),
      offerSentTimestamp: this.coerceToDate(data.offerSentTimestamp) ?? null,
      paymentMethod: (() => {
        const raw = (data.paymentMethod ?? 'cash');
        const lower = typeof raw === 'string' ? raw.toLowerCase() : String(raw).toLowerCase();
        return (lower === 'cash') ? 'cash' : 'card';
      })(),
      paymentStatus: data.paymentStatus || undefined,
      paymentAttemptStatus: data.paymentAttemptStatus || undefined,
      paymentFailureSource: data.paymentFailureSource || undefined,
      paymentFailureReasonLabel: data.paymentFailureReasonLabel || undefined,
      paymentFailedAt: this.coerceToDate(data.paymentFailedAt),
      cmiFailureReason: data.cmiFailureReason || undefined,
      cmiReturnCode: data.cmiReturnCode || undefined,
      promoAmountApplied: data.promoAmountApplied || 0,
      promoCodeApplied: data.promoCodeApplied || null,
      status: data.status || 'PLACED',
      statusTimestamps: {
        placedAt: this.coerceToDate(data.statusTimestamps?.placedAt),
        acceptedAt: this.coerceToDate(data.statusTimestamps?.acceptedAt),
        preparingAt: this.coerceToDate(data.statusTimestamps?.preparingAt),
        readyForPickupAt: this.coerceToDate(data.statusTimestamps?.readyForPickupAt),
        acceptedByDriverAt: this.coerceToDate(data.statusTimestamps?.acceptedByDriverAt),
        deliveringAt: this.coerceToDate(data.statusTimestamps?.deliveringAt),
        deliveredAt: this.coerceToDate(data.statusTimestamps?.deliveredAt),
        cancelledAt: statusCancelledAt ?? cancellationCancelledAt,
        noDriverFoundAt: this.coerceToDate(data.statusTimestamps?.noDriverFoundAt)
      },
      storeAddress: {
        formatted: data.storeAddress?.formatted || ''
      },
      storeId: data.storeId || '',
      storeLocation: data.storeLocation || [0, 0],
      storeName: data.storeName || '',
      subTotal: data.subTotal || 0,
      totalAmount: data.totalAmount || 0,
      updatedAt: this.coerceToDate(data.updatedAt) ?? new Date(),
      userId: data.userId || '',
      // Snapshot client identity if present on the order doc (fallback when user profile is deleted)
      clientName: data.clientName || undefined,
      clientPhone: data.clientPhone || undefined,
      clientEmail: data.clientEmail || undefined,
      cancellation: data.cancellation ? {
        reasonCode: data.cancellation.reasonCode || undefined,
        reasonText: data.cancellation.reasonText || undefined,
        cancelledAt: cancellationCancelledAt,
        cancelledBy: data.cancellation.cancelledBy ? {
          role: data.cancellation.cancelledBy.role,
          userId: data.cancellation.cancelledBy.userId,
          storeId: data.cancellation.cancelledBy.storeId
        } : undefined
      } : undefined
    };
  }

  // Enrichir les ordres avec les informations client et produits
  private async enrichOrders(
    orders: Order[],
    options: Required<GetOrdersOptions>
  ): Promise<Order[]> {
    // Récupérer tous les IDs de produits uniques
    const productIds = new Set<string>();
    if (options.enrichProducts) {
      orders.forEach(order => {
        order.items.forEach(item => {
          if (item.productId) {
            productIds.add(item.productId);
          }
        });
      });
    }

    // Récupérer tous les IDs d'utilisateurs uniques
    const userIds = new Set<string>();
    if (options.enrichUsers) {
      orders.forEach(order => {
        const needsUserLookup =
          !order.clientName?.trim() || !order.clientPhone?.trim();
        if (needsUserLookup && order.userId) {
          userIds.add(order.userId);
        }
      });
    }

    const productNames = options.enrichProducts
      ? await this.getProductNamesByIds(Array.from(productIds))
      : new Map<string, string>();

    // Récupérer les informations des utilisateurs
    const users =
      options.enrichUsers && userIds.size > 0
        ? await userService.getUsersByIds(Array.from(userIds))
        : new Map<string, User>();
    
    return orders.map(order => ({
      ...order,
      clientName: options.enrichUsers
        ? this.extractClientName(order, users.get(order.userId))
        : order.clientName,
      clientPhone: options.enrichUsers
        ? this.extractClientPhone(order, users.get(order.userId))
        : order.clientPhone,
      productNames: options.enrichProducts
        ? this.extractProductNames(order, productNames)
        : order.productNames
    }));
  }

  private async getProductNamesByIds(productIds: string[]): Promise<Map<string, string>> {
    const productNames = new Map<string, string>();
    const uniqueIds = Array.from(
      new Set(productIds.filter((productId) => productId && productId.trim().length > 0))
    );
    const missingIds: string[] = [];

    uniqueIds.forEach((productId) => {
      const cachedName = this.productNameCache.get(productId);
      if (cachedName) {
        productNames.set(productId, cachedName);
      } else {
        missingIds.push(productId);
      }
    });

    if (missingIds.length === 0) {
      return productNames;
    }

    try {
      const chunkSize = 30;
      const chunks: string[][] = [];
      for (let index = 0; index < missingIds.length; index += chunkSize) {
        chunks.push(missingIds.slice(index, index + chunkSize));
      }

      const snapshots = await Promise.all(
        chunks.map((chunk) =>
          getDocs(
            query(
              collection(db, 'products'),
              where(documentId(), 'in', chunk)
            )
          ).then((snapshot) => ({ chunk, snapshot }))
        )
      );

      snapshots.forEach(({ chunk, snapshot }) => {
        const fetchedIds = new Set<string>();

        snapshot.docs.forEach((productDoc) => {
          const productData = productDoc.data();
          const productName = productData.name || this.unknownProductName;
          this.productNameCache.set(productDoc.id, productName);
          productNames.set(productDoc.id, productName);
          fetchedIds.add(productDoc.id);
        });

        chunk.forEach((productId) => {
          if (!fetchedIds.has(productId)) {
            this.productNameCache.set(productId, this.unknownProductName);
            productNames.set(productId, this.unknownProductName);
          }
        });
      });
    } catch (error) {
      console.error('Erreur lors de la récupération des noms de produits:', error);
    }

    return productNames;
  }

  private extractClientName(order: Order, user?: User): string {
    // 1) Prioriser le snapshot stocké dans la commande (nouveaux champs ajoutés côté CF)
    if (order.clientName && order.clientName.trim().length > 0) {
      return order.clientName;
    }

    // 2) Si l'utilisateur existe encore, utiliser ses informations
    if (user) {
      return userService.getFullName(user);
    }

    // 3) Pour les commandes avec livraison, fallback sur infos du livreur/adresse
    if (order.deliveryPerson?.firstName && order.deliveryPerson?.lastName) {
      return `${order.deliveryPerson.firstName} ${order.deliveryPerson.lastName}`;
    }

    // 4) Essayer d'extraire depuis l'adresse (ex: première partie formatée)
    if (order.clientAddress.formatted) {
      const addressParts = order.clientAddress.formatted.split(',');
      const name = addressParts[0]?.trim();
      if (name) {
        return name;
      }
    }

    return 'Client inconnu';
  }

  private extractClientPhone(order: Order, user?: User): string {
    // 1) Prioriser le snapshot stocké dans la commande
    if (order.clientPhone && order.clientPhone.trim().length > 0) {
      return order.clientPhone;
    }

    // 2) Si l'utilisateur existe, utiliser son téléphone
    if (user) {
      return userService.getFormattedPhone(user);
    }

    // 3) Fallback: téléphone du livreur si disponible
    if (order.deliveryPerson?.phone) {
      return order.deliveryPerson.phone;
    }

    return 'Non disponible';
  }

  private extractProductNames(order: Order, productNames: Map<string, string>): string[] {
    return order.items.map(item => {
      const itemWithSnapshot = item as typeof item & { name?: string; productName?: string };
      const snapshotName = itemWithSnapshot.productName || itemWithSnapshot.name;
      const productName =
        snapshotName?.trim() ||
        productNames.get(item.productId) ||
        this.unknownProductName;
      let description = `${item.quantity}x ${productName}`;
      
      // Ajouter la taille si différente de normal
      if (item.size && item.size !== 'normal') {
        description += ` (${item.size})`;
      }
      
      return description;
    });
  }

  // Récupérer tous les ordres avec filtres
  async getOrders(
    filters?: OrderFilters,
    options?: GetOrdersOptions
  ): Promise<Order[]> {
    try {
      const resolvedOptions: Required<GetOrdersOptions> = {
        enrichUsers: options?.enrichUsers ?? true,
        enrichProducts: options?.enrichProducts ?? true,
      };
      const constraints: QueryConstraint[] = [];
      let usedIn = false;
      const clientSideFilters: { status?: OrderStatus[]; paymentMethod?: PaymentMethod[]; deliveryMethod?: DeliveryMethod[] } = {};

      // Filtre par date
      if (filters?.dateRange) {
        constraints.push(
          where('createdAt', '>=', Timestamp.fromDate(filters.dateRange.start)),
          where('createdAt', '<=', Timestamp.fromDate(filters.dateRange.end))
        );
      }

      // Filtre par statut
      if (filters?.status && filters.status.length > 0) {
        if (filters.status.length === 1) {
          constraints.push(where('status', '==', filters.status[0]));
        } else if (!usedIn) {
          constraints.push(where('status', 'in', filters.status));
          usedIn = true;
        } else {
          clientSideFilters.status = filters.status;
        }
      }

      // Filtre par méthode de paiement (normalisé côté client pour gérer variations de casse/valeurs)
      if (filters?.paymentMethod && filters.paymentMethod.length > 0) {
        clientSideFilters.paymentMethod = filters.paymentMethod;
      }

      // Filtre par méthode de livraison
      if (filters?.deliveryMethod && filters.deliveryMethod.length > 0) {
        if (filters.deliveryMethod.length === 1) {
          constraints.push(where('deliveryMethod', '==', filters.deliveryMethod[0]));
        } else if (!usedIn) {
          constraints.push(where('deliveryMethod', 'in', filters.deliveryMethod));
          usedIn = true;
        } else {
          clientSideFilters.deliveryMethod = filters.deliveryMethod;
        }
      }

      // Filtre par magasin
      if (filters?.storeId) {
        constraints.push(where('storeId', '==', filters.storeId));
      }

      // Filtre par driver
      if (filters?.driverId) {
        constraints.push(where('deliveryPerson.id', '==', filters.driverId));
      }

      // Filtre par code promo appliqué
      if (filters?.promoCode && filters.promoCode.trim().length > 0) {
        constraints.push(where('promoCodeApplied', '==', filters.promoCode.trim()));
      }

      // Tri par défaut par date de création (plus récent en premier)
      constraints.push(orderBy('createdAt', 'desc'));

      const q = query(collection(db, this.collectionName), ...constraints);
      const querySnapshot = await getDocs(q);
      
      const orders = querySnapshot.docs.map(doc => this.convertFirestoreToOrder(doc));
      let result =
        resolvedOptions.enrichUsers || resolvedOptions.enrichProducts
          ? await this.enrichOrders(orders, resolvedOptions)
          : orders;

      // Appliquer les filtres côté client si nécessaire
      if (clientSideFilters.status) {
        result = result.filter(o => clientSideFilters.status!.includes(o.status));
      }
      if (clientSideFilters.paymentMethod) {
        result = result.filter(o => clientSideFilters.paymentMethod!.includes(o.paymentMethod));
      }
      if (clientSideFilters.deliveryMethod) {
        result = result.filter(o => clientSideFilters.deliveryMethod!.includes(o.deliveryMethod));
      }
      
      // Appliquer le filtre de recherche côté client
      if (filters?.searchTerm) {
        result = this.filterBySearchTerm(result, filters.searchTerm);
      }

      return result;
    } catch (error) {
      console.error('Erreur lors de la récupération des ordres:', error);
      throw new Error('Impossible de récupérer les ordres');
    }
  }

  // Filtrer par terme de recherche
  private filterBySearchTerm(orders: Order[], searchTerm: string): Order[] {
    const term = searchTerm.toLowerCase();
    return orders.filter(order => 
      order.id.toLowerCase().includes(term) ||
      order.clientName?.toLowerCase().includes(term) ||
      order.clientPhone?.toLowerCase().includes(term) ||
      order.productNames?.some(product => product.toLowerCase().includes(term)) ||
      order.storeName.toLowerCase().includes(term)
    );
  }

  // Trier les ordres
  sortOrders(orders: Order[], sortConfig: OrderSortConfig): Order[] {
    return [...orders].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortConfig.field) {
        case 'clientName':
          aValue = a.clientName || '';
          bValue = b.clientName || '';
          break;
        case 'clientPhone':
          aValue = a.clientPhone || '';
          bValue = b.clientPhone || '';
          break;
        case 'products':
          aValue = a.productNames?.join(', ') || '';
          bValue = b.productNames?.join(', ') || '';
          break;
        case 'paymentMethod':
          aValue = a.paymentMethod;
          bValue = b.paymentMethod;
          break;
        case 'deliveryMethod':
          aValue = a.deliveryMethod;
          bValue = b.deliveryMethod;
          break;
        case 'driverName':
          aValue = a.deliveryPerson ? `${a.deliveryPerson.firstName} ${a.deliveryPerson.lastName}` : '';
          bValue = b.deliveryPerson ? `${b.deliveryPerson.firstName} ${b.deliveryPerson.lastName}` : '';
          break;
        case 'createdAt':
          aValue = a.createdAt.getTime();
          bValue = b.createdAt.getTime();
          break;
        case 'totalAmount':
          aValue = a.totalAmount;
          bValue = b.totalAmount;
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        default:
          return 0;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }

  // Récupérer les ordres par plage de dates
  async getOrdersByDateRange(startDate: Date, endDate: Date): Promise<Order[]> {
    return this.getOrders({
      dateRange: { start: startDate, end: endDate }
    });
  }

  // Récupérer les ordres par magasin
  async getOrdersByStore(storeId: string): Promise<Order[]> {
    return this.getOrders({ storeId });
  }

  // Récupérer les ordres par driver
  async getOrdersByDriver(driverId: string): Promise<Order[]> {
    return this.getOrders({ driverId });
  }

  // Pagination helper: list orders by userId with server-side constraints
  async listOrdersByUser(
    userId: string,
    pageSize: number = 20,
    cursor?: DocumentSnapshot
  ): Promise<{ orders: Order[]; nextCursor?: DocumentSnapshot }> {
    try {
      const constraints: QueryConstraint[] = [
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(pageSize),
      ];
      if (cursor) {
        constraints.push(startAfter(cursor));
      }
      const q = query(collection(db, this.collectionName), ...constraints);
      const snap = await getDocs(q);

      const rawOrders = snap.docs.map(d => this.convertFirestoreToOrder(d));
      const orders = await this.enrichOrders(rawOrders, {
        enrichUsers: true,
        enrichProducts: true,
      });
      const nextCursor = snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : undefined;

      return { orders, nextCursor };
    } catch (e) {
      console.error('Erreur listOrdersByUser:', e);
      return { orders: [], nextCursor: undefined };
    }
  }

  // Valider si les ordres peuvent être supprimés
  async validateOrdersForDeletion(orderIds: string[]): Promise<ValidationResult> {
    const warnings: string[] = [];
    const blockers: string[] = [];

    try {
      for (const orderId of orderIds) {
        const orderDoc = await getDoc(doc(db, this.collectionName, orderId));
        
        if (!orderDoc.exists()) {
          warnings.push(`L'ordre ${orderId} n'existe plus`);
          continue;
        }

        const order = this.convertFirestoreToOrder(orderDoc);
        
        // Vérifier si l'ordre est en cours de livraison
        if (['ACCEPTED_BY_DRIVER', 'DELIVERING'].includes(order.status)) {
          blockers.push(`L'ordre ${orderId} est en cours de livraison et ne peut pas être supprimé`);
        }

        // Avertir pour les ordres récents
        const hoursSinceCreation = (Date.now() - order.createdAt.getTime()) / (1000 * 60 * 60);
        if (hoursSinceCreation < 1) {
          warnings.push(`L'ordre ${orderId} a été créé il y a moins d'une heure`);
        }
      }

      return {
        canDelete: blockers.length === 0,
        warnings,
        blockers
      };
    } catch (error) {
      console.error('Erreur lors de la validation:', error);
      return {
        canDelete: false,
        warnings: [],
        blockers: ['Erreur lors de la validation des ordres']
      };
    }
  }

  // Supprimer un ordre (archiver avant suppression)
  async deleteOrder(orderId: string): Promise<void> {
    try {
      const orderRef = doc(db, this.collectionName, orderId);
      const snap = await getDoc(orderRef);

      // Si l'ordre n'existe pas, s'assurer qu'aucune référence orpheline ne reste et sortir
      if (!snap.exists()) {
        await deleteDoc(orderRef);
        return;
      }

      const user = authService.getCurrentUser();
      const deletedBy: { uid: string; email?: string | null; displayName?: string | null } = {
        uid: user?.uid ?? 'unknown',
      };
      if (user?.email) deletedBy.email = user.email;
      if (user?.displayName) deletedBy.displayName = user.displayName;

      const archiveRef = doc(db, 'orders_deleted', orderId);
      const batch = writeBatch(db);

      batch.set(archiveRef, {
        originalId: orderId,
        deletedAt: serverTimestamp(),
        deletedBy,
        original: snap.data(),
      });

      batch.delete(orderRef);

      await batch.commit();
    } catch (error) {
      console.error(`Erreur lors de l'archivage/suppression de l'ordre ${orderId}:`, error);
      throw new Error(`Impossible de supprimer l'ordre ${orderId}`);
    }
  }

  // Supprimer plusieurs ordres
  async deleteMultipleOrders(orderIds: string[]): Promise<DeleteResult> {
    const result: DeleteResult = {
      success: true,
      deletedCount: 0,
      failedCount: 0,
      errors: []
    };

    for (const orderId of orderIds) {
      try {
        await this.deleteOrder(orderId);
        result.deletedCount++;
      } catch (error) {
        result.failedCount++;
        result.errors.push(`Échec de suppression de l'ordre ${orderId}: ${error}`);
      }
    }

    result.success = result.failedCount === 0;
    return result;
  }

  // Obtenir un résumé d'ordre
  async getOrderSummary(orderId: string): Promise<OrderSummary> {
    try {
      const orderDoc = await getDoc(doc(db, this.collectionName, orderId));
      
      if (!orderDoc.exists()) {
        throw new Error(`L'ordre ${orderId} n'existe pas`);
      }

      const order = this.convertFirestoreToOrder(orderDoc);
      const enrichedOrders = await this.enrichOrders([order], {
        enrichUsers: true,
        enrichProducts: true,
      });
      const enrichedOrder = enrichedOrders[0];

      return {
        id: enrichedOrder.id,
        clientName: enrichedOrder.clientName || 'Client inconnu',
        totalAmount: enrichedOrder.totalAmount,
        createdAt: enrichedOrder.createdAt,
        status: enrichedOrder.status
      };
    } catch (error) {
      console.error(`Erreur lors de la récupération du résumé de l'ordre ${orderId}:`, error);
      throw error;
    }
  }

  // Vérifier si un ordre peut être supprimé
  async canDeleteOrder(orderId: string): Promise<boolean> {
    const validation = await this.validateOrdersForDeletion([orderId]);
    return validation.canDelete;
  }

  // Obtenir les statistiques des ordres (rapide, sans enrichissement)
  async getOrderStats(filters?: OrderFilters): Promise<{
    totalOrders: number;
    totalRevenue: number;
    averageOrderValue: number;
    ordersByStatus: Record<OrderStatus, number>;
    ordersByPaymentMethod: Record<PaymentMethod, number>;
    ordersByDeliveryMethod: Record<DeliveryMethod, number>;
  }> {
    try {
      const constraints: QueryConstraint[] = [];
      let usedIn = false;
      const clientSideFilters: { status?: OrderStatus[]; paymentMethod?: PaymentMethod[]; deliveryMethod?: DeliveryMethod[] } = {};

      // Filtre par date
      if (filters?.dateRange) {
        constraints.push(
          where('createdAt', '>=', Timestamp.fromDate(filters.dateRange.start)),
          where('createdAt', '<=', Timestamp.fromDate(filters.dateRange.end))
        );
      }

      // Filtre par statut
      if (filters?.status && filters.status.length > 0) {
        if (filters.status.length === 1) {
          constraints.push(where('status', '==', filters.status[0]));
        } else if (!usedIn) {
          constraints.push(where('status', 'in', filters.status));
          usedIn = true;
        } else {
          clientSideFilters.status = filters.status;
        }
      }

      // Filtre par méthode de livraison (server-side si possible)
      if (filters?.deliveryMethod && filters.deliveryMethod.length > 0) {
        if (filters.deliveryMethod.length === 1) {
          constraints.push(where('deliveryMethod', '==', filters.deliveryMethod[0]));
        } else if (!usedIn) {
          constraints.push(where('deliveryMethod', 'in', filters.deliveryMethod));
          usedIn = true;
        } else {
          clientSideFilters.deliveryMethod = filters.deliveryMethod;
        }
      }

      // Filtre par magasin
      if (filters?.storeId) {
        constraints.push(where('storeId', '==', filters.storeId));
      }

      // Filtre par driver
      if (filters?.driverId) {
        constraints.push(where('deliveryPerson.id', '==', filters.driverId));
      }

      // Filtre par code promo appliqué
      if (filters?.promoCode && filters.promoCode.trim().length > 0) {
        constraints.push(where('promoCodeApplied', '==', filters.promoCode.trim()));
      }

      // Tri par défaut par date de création (plus récent en premier)
      constraints.push(orderBy('createdAt', 'desc'));

      const q = query(collection(db, this.collectionName), ...constraints);
      const querySnapshot = await getDocs(q);

      // Agrégation rapide (sans enrichissement)
      const stats = {
        totalOrders: 0,
        totalRevenue: 0,
        averageOrderValue: 0,
        ordersByStatus: {} as Record<OrderStatus, number>,
        ordersByPaymentMethod: {} as Record<PaymentMethod, number>,
        ordersByDeliveryMethod: {} as Record<DeliveryMethod, number>
      };

      // Statuts exclus du chiffre d'affaires (commandes non abouties)
      const excludedFromRevenue: Set<string> = new Set(['CANCELLED', 'NO_DRIVER_FOUND', 'PAYMENT_PENDING', 'PAYMENT_FAILED']);
      let revenueOrderCount = 0;

      for (const d of querySnapshot.docs) {
        const data = d.data();

        // Client-side filters non supportés server-side
        if (clientSideFilters.status && !clientSideFilters.status.includes(data.status)) continue;

        // Normalisations légères comme convertFirestoreToOrder
        const pmRaw = (data.paymentMethod ?? 'cash');
        const payment: PaymentMethod = (typeof pmRaw === 'string' && pmRaw.toLowerCase() === 'cash') ? 'cash' : 'card';

        const dmRaw = (data.deliveryMethod ?? 'delivery');
        const delivery: DeliveryMethod = (typeof dmRaw === 'string' && dmRaw.toLowerCase() === 'pickup') ? 'pickup' : 'delivery';

        if (clientSideFilters.deliveryMethod && !clientSideFilters.deliveryMethod.includes(delivery)) continue;

        const total = typeof data.totalAmount === 'number' ? data.totalAmount : 0;
        const status = (data.status || 'PLACED') as OrderStatus;

        // Compter toutes les commandes mais exclure les annulées/échouées du CA
        stats.totalOrders += 1;
        if (!excludedFromRevenue.has(status)) {
          stats.totalRevenue += total;
          revenueOrderCount += 1;
        }

        stats.ordersByStatus[status] = (stats.ordersByStatus[status] || 0) + 1;
        stats.ordersByPaymentMethod[payment] = (stats.ordersByPaymentMethod[payment] || 0) + 1;
        stats.ordersByDeliveryMethod[delivery] = (stats.ordersByDeliveryMethod[delivery] || 0) + 1;
      }

      stats.averageOrderValue = revenueOrderCount > 0 ? stats.totalRevenue / revenueOrderCount : 0;

      return stats;
    } catch (error) {
      console.error('Erreur lors du calcul des statistiques:', error);
      throw new Error('Impossible de calculer les statistiques des ordres');
    }
  }

  // Exporter les ordres en CSV
  exportOrdersToCSV(orders: Order[]): string {
    const headers = [
      'ID',
      'Client',
      'Téléphone',
      'Produits',
      'Montant Total',
      'Méthode de Paiement',
      'Méthode de Livraison',
      'Driver',
      'Statut',
      'Date de Création',
      'Magasin'
    ];

    const rows = orders.map(order => [
      order.id,
      order.clientName || '',
      order.clientPhone || '',
      order.productNames?.join('; ') || '',
      order.totalAmount.toString(),
      order.paymentMethod === 'cash' ? 'Espèces' : 'Carte',
      order.deliveryMethod === 'delivery' ? 'Livraison' : 'À emporter',
      order.deliveryPerson ? `${order.deliveryPerson.firstName} ${order.deliveryPerson.lastName}` : '',
      order.status,
      order.createdAt.toLocaleDateString('fr-FR'),
      order.storeName
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    return csvContent;
  }
}

const orderService = new OrderService();
export default orderService;
